import {
  Component,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslationService, TranslatePipe } from '../../core/i18n';
import { AuthService } from '../../core/auth';
import { ShipmentFlowStateService } from '../../core/services/shipment-flow-state.service';
import {
  alternateLocale,
  alternateLocaleLabel,
  buildFooterLinks,
  buildHeaderNavItems,
  buildMobileNavItems,
  buildSidebarNavItems,
  openSideMenuExternalUrl,
} from '../navigation/app-navigation.config';
import { ShellFooter, type FooterLanguageOption } from '../sections/shell-footer/shell-footer';
import { ShellHeader } from '../sections/shell-header/shell-header';
import { ShellHero } from '../sections/shell-hero/shell-hero';
import { ShellPageContent } from '../sections/shell-page-content/shell-page-content';
import { ShellSidebar } from '../sections/shell-sidebar/shell-sidebar';
import { ShellSupportChat } from '../sections/shell-support-chat/shell-support-chat';
import { ShellSystemAlert } from '../sections/shell-system-alert/shell-system-alert';
import { MaintenanceBannerService } from '../sections/shell-system-alert/maintenance-banner.service';
import { ShellCouponBanner } from '../sections/shell-coupon-banner/shell-coupon-banner';
import { LogoutConfirmationDialog } from '../sections/logout-confirmation-dialog/logout-confirmation-dialog';

/** Header switches from the top product nav to the hamburger drawer below this width. */
const COMPACT_NAV_QUERY = '(max-width: 1199px)';

/** Fine-grained thresholds so the footer observer updates the chat lift smoothly while scrolling. */
const FOOTER_LIFT_THRESHOLDS = Array.from({ length: 51 }, (_, step) => step / 50);

/**
 * Application shell composing header, sidebar, hero, alert, footer, and routed content sections.
 */
@Component({
  selector: 'app-shell',
  imports: [
    TranslatePipe,
    ShellHeader,
    ShellSidebar,
    ShellHero,
    ShellSystemAlert,
    ShellCouponBanner,
    ShellPageContent,
    ShellFooter,
    ShellSupportChat,
    LogoutConfirmationDialog,
  ],
  templateUrl: './app-shell.html',
  styleUrl: './app-shell.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AppShell {
  private readonly translation = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly maintenanceBannerService = inject(MaintenanceBannerService);

  protected readonly alertVisible = signal(true);
  protected readonly maintenanceBanner = this.maintenanceBannerService.banner;
  /** Shown when the shipper session includes an active coupon, or when `?couponBanner=1` is present. */
  protected readonly couponBannerVisible = signal(this.readCouponBannerQueryFlag());
  /** Controls the OM-720 logout confirmation warning popup. */
  protected readonly logoutConfirmOpen = signal(false);
  private readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /**
   * True only on Ship From/Ship To (`/`). Hero and system alert match Figma on later wizard steps by staying hidden.
   */
  protected readonly shellBannersVisible = computed(() => {
    const path = this.currentPath().split(/[?#]/, 1)[0];
    return path === '/' || path === '';
  });

  /** True below the compact breakpoint, where the top nav collapses into the hamburger drawer. */
  private readonly compact = signal(false);

  /** Footer host element, observed to lift the floating chat button clear of the footer. */
  private readonly footerRef = viewChild('footerRef', { read: ElementRef<HTMLElement> });

  /** Pixels to raise the floating chat button so it never overlaps the footer as it scrolls in. */
  protected readonly chatLift = signal(0);

  constructor() {
    this.persistCouponFromQuery();
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const query = window.matchMedia(COMPACT_NAV_QUERY);
      this.compact.set(query.matches);
      const onChange = (event: MediaQueryListEvent) => this.compact.set(event.matches);
      query.addEventListener('change', onChange);
      inject(DestroyRef).onDestroy(() => query.removeEventListener('change', onChange));
    }

    const destroyRef = inject(DestroyRef);
    afterNextRender(() => this.trackChatFooterOverlap(destroyRef));
  }

  /**
   * Lifts the fixed chat button by exactly the footer's intrusion into the viewport, so the
   * button keeps floating in the corner but rests just above the footer once it scrolls into view.
   * Browser-only and a no-op where IntersectionObserver is unavailable (e.g. jsdom).
   * @param destroyRef Used to disconnect the observer and listener when the shell is destroyed.
   */
  private trackChatFooterOverlap(destroyRef: DestroyRef): void {
    const footer = this.footerRef()?.nativeElement;
    if (!footer || typeof IntersectionObserver === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const updateLift = () => {
      const rect = footer.getBoundingClientRect();
      const intrusion = window.innerHeight - rect.top;
      if (intrusion <= 0) {
        this.chatLift.set(0);
        return;
      }
      const maxLift = rect.height + 24;
      this.chatLift.set(Math.round(Math.min(intrusion, maxLift)));
    };

    const observer = new IntersectionObserver(() => updateLift(), {
      threshold: FOOTER_LIFT_THRESHOLDS,
    });
    observer.observe(footer);

    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateLift);
    };
    window.addEventListener('resize', onResize);

    destroyRef.onDestroy(() => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(frame);
    });
  }

  protected readonly headerNavItems = computed(() => {
    this.translation.revision();
    const translate = (key: string) => this.translation.translate(key);
    const locale = this.translation.locale();
    return this.compact()
      ? buildMobileNavItems(translate, this.currentPath(), locale)
      : buildHeaderNavItems(translate);
  });

  protected readonly sidebarNavItems = computed(() => {
    this.translation.revision();
    return buildSidebarNavItems(
      (key) => this.translation.translate(key),
      this.currentPath(),
      this.translation.locale(),
    );
  });

  /**
   * Handles desktop sidebar item selection (OM-825).
   * External tools open in a new tab; Create A Shipment navigates within the SPA.
   * @param itemId Sidebar nav item id from puro-sidebar-navigation.
   */
  protected onSidebarItemClick(itemId: string): void {
    if (openSideMenuExternalUrl(itemId, this.translation.locale())) {
      return;
    }

    if (itemId === 'create-shipment') {
      void this.router.navigateByUrl('/');
    }
  }

  protected readonly languageToggleLabel = computed(() => {
    this.translation.revision();
    return alternateLocaleLabel(this.translation.locale());
  });

  /** Current locale value bound to the footer's native language selector. */
  protected readonly footerLanguage = computed(() => {
    this.translation.revision();
    return this.translation.locale();
  });

  /**
   * Options for the footer language selector.
   * Labels are endonyms (each language in its own name) per the Figma footer, so they are
   * intentionally not translated.
   */
  protected readonly footerLanguages: FooterLanguageOption[] = [
    { label: 'English', value: 'en' },
    { label: 'Français', value: 'fr' },
  ];

  protected readonly footerLinks = computed(() => {
    this.translation.revision();
    return buildFooterLinks((key) => this.translation.translate(key), this.translation.locale());
  });

  protected readonly footerCopyright = computed(() => {
    this.translation.revision();
    return this.translation.translate('layout.footer.copyright', {
      year: String(new Date().getFullYear()),
    });
  });

  /**
   * Switches between English and French when a language control is activated.
   */
  protected async toggleLanguage(): Promise<void> {
    await this.translation.setLocale(alternateLocale(this.translation.locale()));
  }

  /**
   * Applies the locale chosen from the footer language selector.
   * @param value Selected option value emitted by the kit footer ('en' or 'fr').
   */
  protected async onFooterLanguageChange(value: string): Promise<void> {
    await this.translation.setLocale(value === 'fr' ? 'fr' : 'en');
  }

  /**
   * Opens the logout confirmation dialog from the profile menu (OM-720).
   */
  protected onLogOut(): void {
    this.logoutConfirmOpen.set(true);
  }

  /**
   * Dismisses the logout confirmation without ending the session or clearing wizard state.
   */
  protected onLogoutConfirmCancelled(): void {
    this.logoutConfirmOpen.set(false);
  }

  /**
   * Clears unfinished shipment wizard state, then ends the local session and Okta sign-out.
   */
  protected onLogoutConfirmed(): void {
    this.logoutConfirmOpen.set(false);
    this.shipmentFlowState.clear();
    void this.auth.logout();
  }

  /**
   * Hides the active maintenance notification for the current application session.
   */
  protected dismissAlert(): void {
    this.alertVisible.set(false);
  }

  /**
   * Reads `?couponBanner=1` so E2E and manual QA can exercise the success banner without a live coupon session.
   */
  private readCouponBannerQueryFlag(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return new URLSearchParams(window.location.search).get('couponBanner') === '1';
  }

  /**
   * Persists a coupon session from `couponCode` and `couponEmail` query params when both are present.
   */
  private persistCouponFromQuery(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const couponCode = params.get('couponCode')?.trim();
    const email = params.get('couponEmail')?.trim();
    if (!couponCode || !email) {
      return;
    }

    this.shipmentFlowState.saveCoupon({ couponCode, email });
  }
}
